<template>
	<div class="card">
		<header class="card-header language-list">
			<h3 class="card-title">General Settings</h3>
		</header>
		<div class="card-body">
			<div class="alert alert-primary alert--small" v-if="isScheduled">
				<div class="alert-icon fas fa-info-circle"></div>
				<div class="alert-content">
					<h4>You are editing a scheduled post</h4>
					<p>If you save this article with a future displayDate, it will be published on that day, even if its only a draft now!</p>
				</div>
			</div>
			<div class="alert alert-primary alert--small" v-if="!editorGeneral.isArchived">
				<div class="alert-icon fas fa-info-circle"></div>
				<div class="alert-content">
					<h4>You are editing a draft.</h4>
					<p>An editor needs to approve your changes before going live.</p>
				</div>
			</div>
			<div class="alert alert-primary alert--small" v-if="editorGeneral.isArchived">
				<div class="alert-icon fas fa-archive"></div>
				<div class="alert-content">
					<h4>This is an Archived article.</h4>
					<p>This article has been archived for future reference and is not live on the site.</p>
				</div>
			</div>
			<section class="publishing-tools">
				<button @click.prevent="saveDraft" class="btn btn-primary mb-1">
					<i class="fas fa-save"></i> Save Draft
				</button>
				<button @click.prevent="publish" class="btn btn-success" v-if="!isScheduled && editorGeneral.id && userRoles.includes('articles.editor') && !userRoles.includes('users.translator')">
					<i class="fas fa-check"></i> Save & Publish
				</button>
				<button @click.prevent="publish" class="btn btn-success" v-if="isScheduled && editorGeneral.id && userRoles.includes('articles.editor') && !userRoles.includes('users.translator')">
					<i class="fas fa-check"></i> Save & Schedule
				</button>
				<template v-if="editorGeneral.id">
					<div class="action-links">
						<a :href="generatedUrl + '/draft?nocache=true&hash=' + authPixelHash" target="_blank">Preview Draft</a>
						<a :href="generatedUrl" target="_blank">Open Live</a>
						<a href="#" @click.prevent="deleteDraft(editorGeneral.id)" class="text-danger" v-if="editorGeneral.isDraft && !userRoles.includes('users.translator')">Delete Draft</a>
					</div>
				</template>
			</section>
			<section class="mb-3" v-if="!userRoles.includes('users.translator')">
				<h4 class="card-subtitle mt-3">Publishing Tools</h4>
				<div class="form-group">
					<label for="author">Author</label>
					<select id="author" v-model="editorGeneral.author_id" @change="isSaveDraftDisabled = false">
						<option :value="null">Please select an Author</option>
						<option :value="author.id" v-for="author in editorGeneral.authors">{{ author.name }}</option>
					</select>
					<p class="input-error" v-if="errors.errors['articleEditorGeneral_author_id']">{{ errors.errors['articleEditorGeneral_author_id'] }}</p>
				</div>
				<div class="form-group">
					<label for="providerType">Article Provider Type</label>
					<select id="providerType" v-model="editorGeneral.selected_provider_type_id" @change="isSaveDraftDisabled = false" :disabled="editorGeneral.category_id === 2">
						<option :value="null">Please select a provider type/None</option>
						<option :value="type.id" v-for="type in editorGeneral.providerTypes">{{ type.name }}</option>
					</select>
					<p class="input-error" v-if="errors.errors['articleEditorGeneral_selected_provider_type_id']">{{ errors.errors['articleEditorGeneral_selected_provider_type_id'] }}</p>
				</div>
				<div class="form-group">
					<label for="articleCategory">Category</label>
					<select id="articleCategory" name="articleCategory" v-model="editorGeneral.category_id" @change="checkCategory">
						<option :value="null">Please select a Category</option>
						<option :value="category.id" v-for="category in editorGeneral.categories">
							{{ category.title }}
						</option>
					</select>
					<p class="input-error" v-if="errors.errors['articleEditorGeneral_category_id']">{{ errors.errors['articleEditorGeneral_category_id'] }}</p>
				</div>
				<div class="form-group">
					<label for="articleTemplate">Template</label>
					<select id="articleTemplate" name="articleTemplate" v-model="editorGeneral.template" @change="isSaveDraftDisabled = false">
						<option value="post" selected>Normal</option>
						<option value="post-fullwidth">Wide</option>
						<option value="post-fullscreen">Fullscreen</option>
					</select>
					<p class="input-error" v-if="errors.errors['articleEditorGenera_template']">{{ errors.errors['articleEditorGeneral_template'] }}</p>
				</div>
				<div class="form-group">
					<label for="date">Display Date</label>
					<input id="date" type="date" :min="editorGeneral.published_date[clickedLang]" v-model="editorGeneral.display_date[clickedLang]" @change="isSaveDraftDisabled = false">
					<p class="input-error" v-if="errors.errors['articleEditorGeneral_dosplay_date']">{{ errors.errors['articleEditorGeneral_display_date'] }}</p>
				</div>
				<template v-if="editorGeneral.id">
					<footer class="card-footer pt-footer">
						<strong>Last Updated:</strong> {{ editorGeneral.updated_at }} CEST
					</footer>
					<hr>
					<image-uploader
							:data-title="'Featured Image'"
							:data-path="'/articles/'"
							:data-post-url="'/upload/featuredImage'"
							:data-model-id="editorGeneral.id"
							:data-image="editorGeneral.featured_image"
					></image-uploader>
				</template>
			</section>
		</div>
	</div>
</template>

<script>
import {eventBus} from "../../app";

export default {
	props: {
		editorGeneral: {
			required: true
		},
		generatedUrl: {
			type: String
		},
		isSaveDraftDisabled: {
			required: true
		},
		dataAuthPixelHash: {
			type: String
		},
		errors: {},
	},
	data() {
		return {
			userRoles: this.$currentUserRoles,
			clickedLang: this.$defaultLanguage.locale,
			authPixelHash: this.dataAuthPixelHash
		}
	},
	created() {
		let vm = this;
		// When setActiveStatus method ran, change selected data in form fields
		/* From LangSelector */
		eventBus.$on('setActiveStatus', function (clickedLang) {
			vm.clickedLang = clickedLang;
		});
	},
	computed: {
		isScheduled: function () {
			let isScheduled = false;
			if (this.editorGeneral.hasOwnProperty('display_date')) {
				let displayDate = new Date(this.editorGeneral.display_date[this.clickedLang]);
				let currentDate = new Date();
				isScheduled = currentDate.getTime() < displayDate.getTime();
			} // if

			this.editorGeneral.isScheduled = isScheduled;
			return isScheduled;
		},
	},
	methods: {
		deleteDraft: function () {
			let vm = this;
			eventBus.$emit('onLoaderStart');
			vm.$dialog.confirm('Do you really want to delete the article draft?', {
				loader: true
			}).then(function (dialog) {
				axios.delete('/api/' + vm.$selectedSite.slug + '/articles/' + vm.editorGeneral.id + '/deleteDraft')
						.then(function () {
							eventBus.$emit('onLoaderFinish');
							vm.$toastr.success('Article draft deleted.', 'Success');
							if (!vm.editorGeneral.hasPublished) {
								window.onbeforeunload = null;
								window.location.replace(process.env.MIX_APP_URL + vm.$selectedSite.slug + '/articles');
							}
						})
						.catch(function (e) {
							eventBus.$emit('onLoaderFinish');
							vm.$toastr.error('Article draft delete failed.', 'Error');
							vm.errors.record(e.response.data);
						});
				dialog.close();
			}).catch(function () {
				eventBus.$emit('onLoaderFinish');
			});
		},
		saveDraft: function () {
			eventBus.$emit('onLoaderStart');
			eventBus.$emit('onArticleDraftSave');
		},
		publish: function () {
			eventBus.$emit('onLoaderStart');
			eventBus.$emit('onArticlePublish');
		},
		checkCategory: function() {
			if (this.editorGeneral.category_id === 2) {
				this.editorGeneral.selected_provider_type_id = null;
			} // if

			this.isSaveDraftDisabled = false
		},
	}
}
</script>

<style scoped>

</style>
